import React, { useState } from "react";
import { Mail, Phone, MapPin, Send, Calendar as CalIcon, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useDocumentSeo, useJsonLd } from "../lib/seo";

const SERVICES = [
    "Administrative & Executive Support",
    "Business Strategy & Consulting",
    "Document Organization & Data Management",
    "Scheduling & Client Coordination",
    "Business Formation & Startup Structuring",
    "Workforce Training & OJT Support",
    "Other / Not Sure",
];
const TIMES = ["9:00 AM", "10:00 AM", "11:00 AM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"];

export default function Contact() {
    const [tab, setTab] = useState("contact");
    useDocumentSeo({
        title: "Contact & Book a Consultation — TT Admins Business Services",
        description: "Contact Admin This! N Thatz Syncd, LLC to inquire about administrative support, business consulting, or book a free consultation. Call (972) 942-9022 or email cslimp@ttadmins.com.",
        canonical: "https://www.ttadmins.com/contact",
    });
    useJsonLd({
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "Contact Admin This! N Thatz Syncd, LLC",
        url: "https://www.ttadmins.com/contact",
        mainEntity: {
            "@type": "Organization",
            name: "Admin This! N Thatz Syncd, LLC",
            telephone: "+1-972-942-9022",
            email: "cslimp@ttadmins.com",
            address: {
                "@type": "PostalAddress",
                streetAddress: "1920 Lakepointe Dr",
                addressLocality: "Lewisville",
                addressRegion: "TX",
                postalCode: "75057",
                addressCountry: "US",
            },
            contactPoint: {
                "@type": "ContactPoint",
                telephone: "+1-972-942-9022",
                email: "cslimp@ttadmins.com",
                contactType: "customer service",
                areaServed: "US",
                availableLanguage: ["English"],
            },
        },
    });
    return (
        <div data-testid="contact-page">
            <section className="dawn-wash">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
                    <div className="max-w-3xl">
                        <div className="eyebrow mb-3">Connect</div>
                        <h1 className="font-serif text-5xl sm:text-6xl leading-[1.05] font-light text-[var(--brand-navy)]">
                            Let's start a <span className="italic text-[var(--brand-coral)]">conversation</span>.
                        </h1>
                        <p className="mt-5 text-lg text-slate-700 max-w-2xl">
                            Whether you'd like to inquire about a service or book a consultation, we're listening — prayerfully and promptly.
                        </p>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-12 gap-12">
                <aside className="lg:col-span-4 space-y-5">
                    <InfoCard icon={<Mail />} title="Email" value="cslimp@ttadmins.com" href="mailto:cslimp@ttadmins.com" />
                    <InfoCard icon={<Phone />} title="Phone" value="(972) 942-9022" href="tel:+19729429022" />
                    <InfoCard icon={<MapPin />} title="Office" value="1920 Lakepointe Dr, Lewisville, TX 75057" />
                </aside>

                <div className="lg:col-span-8">
                    <div className="flex gap-2 mb-6 border-b border-slate-200" data-testid="contact-tabs">
                        <TabBtn active={tab === "contact"} onClick={() => setTab("contact")} testid="tab-contact">Send a Message</TabBtn>
                        <TabBtn active={tab === "booking"} onClick={() => setTab("booking")} testid="tab-booking">Book a Consultation</TabBtn>
                    </div>
                    {tab === "contact" ? <ContactForm /> : <BookingForm />}
                </div>
            </section>
        </div>
    );
}

function TabBtn({ active, onClick, children, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`px-5 py-3 text-sm font-semibold relative ${
                active ? "text-[var(--brand-navy)]" : "text-slate-500 hover:text-slate-700"
            }`}
        >
            {children}
            {active && <span className="absolute -bottom-px left-0 right-0 h-0.5 bg-[var(--brand-coral)]" />}
        </button>
    );
}

function InfoCard({ icon, title, value, href }) {
    const inner = (
        <div className="faith-card p-6 flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-[var(--brand-gold)]/15 text-[var(--brand-gold-deep)] flex items-center justify-center shrink-0">
                {icon}
            </div>
            <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">{title}</div>
                <div className="text-[var(--brand-navy)] font-medium">{value}</div>
            </div>
        </div>
    );
    return href ? <a href={href}>{inner}</a> : inner;
}

function ContactForm() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", service_interest: "", message: "" });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/contact", form);
            toast.success("Thank you! Your message has been received. We'll be in touch.");
            setForm({ name: "", email: "", phone: "", service_interest: "", message: "" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit");
        } finally {
            setLoading(false);
        }
    };

    return (
        <form onSubmit={submit} className="faith-card p-8" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <Field label="Full Name *">
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                        className="input" data-testid="contact-name" />
                </Field>
                <Field label="Email *">
                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                        className="input" data-testid="contact-email" />
                </Field>
                <Field label="Phone (optional)">
                    <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                        className="input" data-testid="contact-phone" />
                </Field>
                <Field label="Service Interest">
                    <select value={form.service_interest} onChange={(e) => setForm({ ...form, service_interest: e.target.value })}
                        className="input" data-testid="contact-service">
                        <option value="">Select one (optional)</option>
                        {SERVICES.map((s) => (<option key={s}>{s}</option>))}
                    </select>
                </Field>
            </div>
            <Field label="Your Message *">
                <textarea required rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="input resize-none" placeholder="Share how we can serve you..." data-testid="contact-message" />
            </Field>
            <button type="submit" disabled={loading} className="btn-gold mt-6 disabled:opacity-50" data-testid="contact-submit-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Send className="w-4 h-4 mr-2" /> Send Message</>}
            </button>
            <style>{`.input { width:100%; padding: 0.75rem 1rem; border-radius: 0.75rem; border: 1px solid #e2e8f0; outline:none; background:white; font-size: 0.95rem; } .input:focus { border-color: var(--brand-gold); box-shadow: 0 0 0 3px rgba(245,185,71,0.15); }`}</style>
        </form>
    );
}

function BookingForm() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", service: SERVICES[0], preferred_date: "", preferred_time: TIMES[0], notes: "" });
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/bookings", form);
            toast.success("Booking request received. We'll confirm by email shortly.");
            setForm({ name: "", email: "", phone: "", service: SERVICES[0], preferred_date: "", preferred_time: TIMES[0], notes: "" });
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit");
        } finally {
            setLoading(false);
        }
    };

    const today = new Date().toISOString().split("T")[0];

    return (
        <form onSubmit={submit} className="faith-card p-8" data-testid="booking-form">
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <Field label="Full Name *">
                    <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" data-testid="booking-name" />
                </Field>
                <Field label="Email *">
                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" data-testid="booking-email" />
                </Field>
                <Field label="Phone (optional)">
                    <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" data-testid="booking-phone" />
                </Field>
                <Field label="Service Needed *">
                    <select required value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="input" data-testid="booking-service">
                        {SERVICES.map((s) => (<option key={s}>{s}</option>))}
                    </select>
                </Field>
                <Field label="Preferred Date *">
                    <input required type="date" min={today} value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className="input" data-testid="booking-date" />
                </Field>
                <Field label="Preferred Time *">
                    <select required value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className="input" data-testid="booking-time">
                        {TIMES.map((t) => (<option key={t}>{t}</option>))}
                    </select>
                </Field>
            </div>
            <Field label="Notes (optional)">
                <textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input resize-none" placeholder="Any context you'd like to share..." data-testid="booking-notes" />
            </Field>
            <button type="submit" disabled={loading} className="btn-gold mt-6 disabled:opacity-50" data-testid="booking-submit-btn">
                {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Booking...</> : <><CalIcon className="w-4 h-4 mr-2" /> Request Consultation</>}
            </button>
            <style>{`.input { width:100%; padding: 0.75rem 1rem; border-radius: 0.75rem; border: 1px solid #e2e8f0; outline:none; background:white; font-size: 0.95rem; } .input:focus { border-color: var(--brand-gold); box-shadow: 0 0 0 3px rgba(245,185,71,0.15); }`}</style>
        </form>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">{label}</span>
            {children}
        </label>
    );
}
